import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../LanguageContext";
import translations from "../../translation_projectdetail";
import { getLocalizedProjects } from "../../data/projects";
import SectionHeader from "../shared/SectionHeader";

function Projects() {
  const { language } = useLanguage();
  const t = translations[language];
  const projects = getLocalizedProjects(t);

  return (
    <section
      id="projects"
      className="mb-16 scroll-mt-16 md:mb-24 lg:mb-36 lg:scroll-mt-24 font-mono"
      aria-label="Selected projects"
    >
      <SectionHeader label="PROJECTS" />

      {/* Projects Grid */}
      <div className="grid gap-6 lg:gap-8">
        {projects.map((project) => (
          <div
            key={project.id}
            className="group relative bg-gradient-to-br from-slate-900/50 to-slate-800/30 border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.15)] backdrop-blur-sm"
          >
            {/* HUD Corner Elements */}
            <div className="absolute top-3 left-3 w-4 h-4 border-l-2 border-t-2 border-cyan-400/60"></div>
            <div className="absolute top-3 right-3 w-4 h-4 border-r-2 border-t-2 border-cyan-400/60"></div>
            <div className="absolute bottom-3 left-3 w-4 h-4 border-l-2 border-b-2 border-cyan-400/60"></div>
            <div className="absolute bottom-3 right-3 w-4 h-4 border-r-2 border-b-2 border-cyan-400/60"></div>

            <div className="grid gap-6 lg:grid-cols-12 lg:gap-8">
              {/* Project Image */}
              <div className="lg:col-span-4">
                <div className="relative overflow-hidden rounded-lg border border-slate-600/50 group-hover:border-cyan-500/50 transition-colors duration-300">
                  <img
                    alt={`${project.title} preview`}
                    loading="lazy"
                    width="400"
                    height="250"
                    decoding="async"
                    className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                    src={project.image}
                  />
                  {/* Image Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {/* Category Badge */}
                  <div className="absolute top-3 left-3 bg-cyan-500/90 text-slate-900 px-2 py-1 rounded text-xs font-bold backdrop-blur-sm">
                    {project.category}
                  </div>
                </div>
              </div>

              {/* Project Info */}
              <div className="lg:col-span-8 flex flex-col justify-between">
                <div>
                  {/* Project Title */}
                  <h3 className="mb-3">
                    <Link
                      className="inline-flex items-center font-bold text-xl text-slate-200 hover:text-cyan-400 transition-colors duration-300 group/link"
                      to={project.link}
                      aria-label={`${project.title} (opens project details)`}
                    >
                      <span className="flex items-center">
                        <span className="font-mono">[</span>
                        <span className="mx-2">{project.title}</span>
                        <span className="font-mono">]</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="inline-block h-4 w-4 shrink-0 transition-transform group-hover/link:translate-x-1 group-hover/link:-translate-y-1 motion-reduce:transition-none ml-2"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    </Link>
                  </h3>

                  {/* Project Description */}
                  <p className="text-slate-300 leading-relaxed mb-4 text-sm">
                    {project.description}
                  </p>
                </div>

                {/* Technologies */}
                <div>
                  <div className="flex items-center mb-3">
                    <span className="text-cyan-400 text-xs font-mono uppercase tracking-wider mr-3">
                      {t.techStackLabel}
                    </span>
                    <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/30 to-transparent"></div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1 text-xs font-medium bg-slate-800/60 text-cyan-300 border border-slate-600/50 rounded-full hover:border-cyan-500/50 hover:bg-slate-700/60 transition-colors duration-300 font-mono"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* View All Projects Link */}
      <div className="mt-2 relative">
        <div className="flex justify-start">
          <a
            className="group/resume relative inline-flex items-center gap-3 px-6 py-3 transition-all duration-300 text-white hover:text-cyan-300"
            href="/"
            aria-label={`${t.viewAllProjects} (opens in a new tab)`}
          >
            <span className="font-mono">{t.viewAllProjects}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-4 h-4 transition-transform duration-200 group-hover/resume:translate-x-1 group-hover/resume:-translate-y-1"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z"
                clipRule="evenodd"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

export default React.memo(Projects);
